import type { TRTCConnection } from 'webrtc/types';

/**
 * AVS-4384: a media element fed a MediaStream whose every video track is FRAMELESS — the remote
 * peer replaced its camera sender with `replaceTrack(null)` when turning the camera off — never
 * leaves HAVE_NOTHING in Chrome: `loadedmetadata` never fires, `play()` stays pending forever
 * (it neither resolves nor rejects), and the stream's AUDIO track is never rendered either. Any
 * surface that (re)mounts a sink while the counterpart's camera is off goes SILENT — which is
 * exactly what a DCC minimize (SingularVoip mounts fresh) or re-expand (VoipPanel remounts its
 * tiles) does. The dealer stops hearing the customer until frames flow again.
 *
 * Detection is not an option: browser-verified, the receiver's video track keeps reporting
 * `muted === false` for at least 12s after `replaceTrack(null)` — Chrome does not fire `mute`
 * for that transition because the transceiver stays active. So the audio never rides the video
 * element at all: it gets its own `<audio>` sink, which has no video track to stall on. The
 * `<video>` keeps the full stream and recovers by itself when frames return (also verified).
 */

/**
 * The returned stream is a SNAPSHOT of the audio tracks. That is safe because the WebRTC layer
 * emits a NEW MediaStream instance whenever the track set changes (`ontrack` in
 * `src/webrtc/index.ts` rebuilds the stream per track), so the caller's memo re-derives on
 * identity change. A future refactor that mutates a stream in place instead would have to add an
 * `addtrack` listener here, or remotes would go silent again.
 */
export const deriveAudioSinkStream = (stream: MediaStream): MediaStream | null => {
  const audioTracks = stream.getAudioTracks();
  // A receive-only or camera-only peer has nothing to sink; mounting an empty element buys nothing.
  if (audioTracks.length === 0) {
    return null;
  }
  return new MediaStream(audioTracks);
};

/**
 * True when `stream` is the local camera's own output, which must NEVER be sinked — playing it
 * back echoes the user's own microphone at them.
 *
 * Reference equality is the normal case: `VoipPanel` hands `<Video>` the very object it read from
 * `myCameraStream()`. But a camera re-grab reassigns that reference, and a tile that re-renders
 * for its OWN reasons (the forced-mute state) in the window before its parent re-renders would
 * still hold the previous stream and misclassify it as a remote — self-echo for as long as the
 * old microphone track stays live. Sharing an audio track is the identity that survives the swap.
 */
export const isOwnCameraStream = (stream: unknown, cameraStream: unknown): boolean => {
  if (stream === cameraStream) {
    return true;
  }
  if (!(stream instanceof MediaStream) || !(cameraStream instanceof MediaStream)) {
    return false;
  }
  const cameraAudioTracks = new Set<MediaStreamTrack>(cameraStream.getAudioTracks());
  return stream.getAudioTracks().some((track) => cameraAudioTracks.has(track));
};

export interface IRemoteAudioSink {
  /** The remote's `connectionId` — the React key, stable across `ontrack` stream rebuilds. */
  id: string;
  /** The remote's CURRENT stream object, untouched; the sink derives its audio-only view itself. */
  stream: MediaStream;
  /** The remote's own mic-mute signal. */
  muted: boolean;
}

/**
 * AVS-4397 — which remotes get an audio sink: every one that carries a MediaStream, taken from the
 * FULL render list and never from the capped tile list, so a layout rule (how many tiles fit on a
 * phone, the `dual` embed) can never decide who is audible. Streamless remotes (camera-denied,
 * receive-only, not yet publishing) have nothing to sink.
 *
 * Sorted by `connectionId` so the DOM order is independent of the active-speaker ranking — a
 * speaker change must not move (let alone remount) a sink. The original stream reference is
 * handed back on purpose: a store tick that spread-copies a connection but keeps `stream` (an
 * active-speaker change, a mute badge) must not look like a new source to the sink.
 */
export const selectRemoteAudioSinks = (remotes: readonly TRTCConnection[]): IRemoteAudioSink[] =>
  remotes
    .filter((remote): remote is TRTCConnection & { stream: MediaStream } => remote.stream instanceof MediaStream)
    .map((remote) => ({ id: remote.connectionId, stream: remote.stream, muted: !!remote.audioMuted }))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
